import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { FC } from 'react';
import type { AxiosInstance } from 'axios';
import { getAtlassianUser } from './user';
import type { AtlassianUser } from './user';
import SentryLogger from '@app/sentry';

interface UserContextType {
  user: AtlassianUser | null;
  /** True once the /me fetch has resolved or rejected */
  isSettled: boolean;
}

const UserContext = createContext<UserContextType | null>(null);

interface UserProviderProps {
  api: AxiosInstance;
  children: React.ReactNode;
}

/**
 * Fetches the authenticated user from /gateway/api/me once on mount
 * and makes it available to all descendants via context.
 * Must be rendered above FeatureGateProvider and AppUpdatesContextProvider.
 * Accepts api as a prop (dependency injection) so @app/auth has no
 * dependency on @app/app, mirroring how AppUpdatesContextProvider also
 * receives api as a prop.
 */
export const UserProvider: FC<UserProviderProps> = ({ api, children }) => {
  const [user, setUser] = useState<AtlassianUser | null>(null);
  const [isSettled, setIsSettled] = useState(false);

  useEffect(() => {
    getAtlassianUser(api)
      .then((fetchedUser) => {
        setUser(fetchedUser);
      })
      .catch((error: unknown) => {
        // Non-401/402 errors are unexpected — log them to Sentry for visibility
        SentryLogger.error(error);
      })
      .finally(() => {
        setIsSettled(true);
      });
    // Run once on mount — we only need to fetch the user once when the app loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({ user, isSettled }), [user, isSettled]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = (): UserContextType => {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error('useUserContext must be used within <UserProvider>');
  }
  return ctx;
};
